<script setup lang="ts">
import { Spinner } from '@/components/document/sub-components/file-manager/sub-components/spinner';
import {
  getFileIcon,
  getOfficeAppLabel,
} from '@/components/document/sub-components/file-manager/views/file-explorer-view/sub-components/render-modes/_shared/file-object/sub-components/file-item/get-file-icon';
import {
  PhArrowSquareOut,
  PhDotsThreeVertical,
  PhDownloadSimple,
  PhEye,
  PhFolder,
  PhFolderOpen,
  PhX,
} from '@phosphor-icons/vue';
import { useResizeObserver } from '@vueuse/core';
import { computed, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFileItemDetailsQuery } from '../../data/useFileItemDetailsQuery';
import { useFileThumbnailQuery } from '../../data/useFileThumbnailQuery';
import { buildSingleFileMenu, type MenuActions } from '../../logic/core/contextMenuBuilders';
import { useFileManagerCopy } from '../../logic/core/useFileManagerCopy';
import { openContextMenuIfAny, useFileManagerContextMenu } from '../../logic/core/useFileManagerContextMenu';
import { useFileManagerDelete } from '../../logic/core/useFileManagerDelete';
import { useFileManagerDetails } from '../../logic/core/useFileManagerDetails';
import { useFileManagerNavigation } from '../../logic/core/useFileManagerNavigation';
import { canPreviewItem, useFileManagerPreview } from '../../logic/core/useFileManagerPreview';
import { useFileManagerRename } from '../../logic/core/useFileManagerRename';
import { useFileManagerTools } from '../../logic/tools/useFileManagerTools';
import { useFileManagerPermissions } from '../../permissions/useFileManagerPermissions';
import { isFolderLike, type FileSystemItem } from '../../types';
import { getParentPath } from '../../utils';
import { formatBytes } from './format-bytes';

const { t } = useI18n();
const { detailsItem, closeDetails } = useFileManagerDetails();
const { openPreview } = useFileManagerPreview();
const { permissionsAt } = useFileManagerPermissions();
const { canDeleteAt, requestDelete } = useFileManagerDelete();
const { canRenameAt, renamingPaths, requestRename } = useFileManagerRename();
const { canCopyAt, copyingPaths, requestCopy } = useFileManagerCopy();
const { navigateTo } = useFileManagerNavigation();
const contextMenu = useFileManagerContextMenu();
// Built during setup(), not inside the click handler: each tool's `run` closes over inject()ed state.
const { toolsFor } = useFileManagerTools();

const parentPath = computed(() => {
  const item = detailsItem.value;
  return item ? getParentPath(item.path) : '';
});

const handleGotoFolder = () => {
  if (detailsItem.value) navigateTo(parentPath.value);
};

// Always true now: "⋮" opens the same single-file menu a grid/tree right-click would (Copy name,
// Rename, Open in X, Delete), and Copy name is unconditional — so the menu can never actually be
// empty. Gating the trigger button itself only made sense back when Delete was its only possible
// entry.
const showMoreButton = true;

const moreButtonRef = useTemplateRef<HTMLButtonElement>('moreButton');

// Tracks whether the shared context menu is currently showing *this* button's menu, not just
// whether some menu is open somewhere — the menu instance is a single shared singleton (also used
// for background/item right-click), so "is anything open" isn't enough to know whether a second
// press of this same button should close it or replace it. Cleared via the watcher below whenever
// the shared menu closes for any reason (outside click, Escape, selecting Delete), so a stale
// `true` here never causes the next press to send a close instead of an open.
const isMoreMenuOpen = ref(false);
watch(contextMenu.isOpen, (open) => {
  if (!open) isMoreMenuOpen.value = false;
});

// Tucked behind a menu instead of sitting as its own always-visible button: a bare Delete button
// at the bottom of the panel was an easy accidental click (e.g. a notification popping in right as
// the user reaches for something else nearby), so it's demoted to a deliberate two-step action
// like the item-level context menu's own Delete entry.
//
// stopPropagation is load-bearing: ContextMenu's own outside-click listener is `window`-level and
// (once mounted) sees every click, including this one. Right-click's equivalent trigger never hits
// that listener because 'contextmenu' isn't 'click' — but this button opens on a real 'click', so
// without stopping it here, the very click that opens the menu also bubbles to `window` and reads
// as "clicked outside the menu", closing it before it's ever visible.
const handleMoreOptions = (event: MouseEvent) => {
  event.stopPropagation();
  if (isMoreMenuOpen.value) {
    contextMenu.close();
    isMoreMenuOpen.value = false;
    return;
  }
  const item = detailsItem.value;
  if (!item) return;
  const rect = moreButtonRef.value?.getBoundingClientRect();
  const position = rect ? { x: rect.right, y: rect.bottom } : { x: 0, y: 0 };
  // Reuses the exact same single-file builder a grid/tree right-click on this item would produce
  // (the details panel is file-only in practice, so buildSingleFolderMenu never applies here) —
  // "Open in SharePoint" then deliberately duplicates the pill button above; see plan.
  //
  // This site calls buildSingleFileMenu DIRECTLY rather than dispatching through contextMenuBuilders, so
  // it is the one that has to be remembered whenever a slot is added. Symptom of forgetting: Verktyg
  // appears in the grid but not in this "⋮".
  const tools = toolsFor([item]);
  const actions: MenuActions = {
    rename:
      canRenameAt(item.path) && !renamingPaths.value.has(item.path)
        ? { request: () => requestRename(item) }
        : undefined,
    // Gated on the parent (where the duplicate lands) and hidden while a copy of this item is already
    // running — same reasoning as the grid's own copy gate.
    copy:
      canCopyAt(parentPath.value) && !copyingPaths.value.has(item.path)
        ? { request: () => requestCopy(item) }
        : undefined,
    tools: tools.length ? { items: tools } : undefined,
    delete: canDeleteAt(item.path)
      ? { request: () => requestDelete([item]) }
      : undefined,
  };
  const menuItems = buildSingleFileMenu([item], actions);
  const opened = openContextMenuIfAny(position, menuItems, contextMenu);
  if (opened) isMoreMenuOpen.value = true;
};

// Previewability is independent of thumbnail-load state (spinner/thumbnail/icon-fallback are all
// equally clickable) — it's the shared canPreviewItem gate, the same one a double-clicked grid row
// is resolved through, so the two entry points cannot drift apart.
const previewClickable = computed(() =>
  canPreviewItem(detailsItem.value, permissionsAt(detailsItem.value?.path ?? '/')),
);

// The item is named explicitly rather than left implicit in the preview state; the null check is
// only for the compiler, :disabled="!previewClickable" already implying a non-null item.
const handlePreview = () => {
  if (detailsItem.value) openPreview(detailsItem.value);
};

const emit = defineEmits<{
  (e: 'open-in-sharepoint', item: FileSystemItem): void;
}>();

const icon = computed(() => {
  const item = detailsItem.value;
  if (!item) return null;
  if (isFolderLike(item)) {
    return { component: PhFolder, color: 'var(--primary-color)' };
  }
  const fileIcon = getFileIcon(item.name);
  return { component: fileIcon.icon, color: fileIcon.color };
});

const { data: thumbnail, isLoading: isThumbnailQueryLoading } =
  useFileThumbnailQuery(detailsItem);
const thumbnailUrl = computed(() => thumbnail.value?.url ?? null);

const { data: itemDetails, isLoading: isDetailsLoading } =
  useFileItemDetailsQuery(detailsItem);

const modifiedDate = computed(() => {
  const lastModifiedDateTime = itemDetails.value?.lastModifiedDateTime;
  return lastModifiedDateTime
    ? new Date(lastModifiedDateTime).toLocaleDateString('sv-SE')
    : null;
});

// Derived locally and synchronously from the item's name — same extraction as get-file-icon.ts,
// just uppercased instead of lowercased. Independent of the async item-details fetch, since it
// doesn't need Graph data at all.
const fileExtension = computed(
  () => detailsItem.value?.name.split('.').pop()?.toUpperCase() ?? '',
);

// Hidden entirely for directories (no downloadUrl ever exists for a folder). For files, shown
// while the details query is still in flight (disabled) so the button doesn't pop in/out, and
// once resolved, only if Graph actually returned a downloadUrl.
const showDownloadButton = computed(() => {
  const item = detailsItem.value;
  if (!item || item.type !== 'file') return false;
  return isDetailsLoading.value || !!itemDetails.value?.downloadUrl;
});

// Hide-on-missing, same rule as the item-level "Open in SharePoint" context-menu action: a broken
// action is omitted entirely rather than shown disabled. Unlike the download button, webUrl is
// eager (comes back with the directory listing itself), so there's no loading state to bridge.
// File-only, matching the context menu: a user right-clicking a folder sees no action, so the
// side-panel button for that same folder must not offer one either.
const showOpenButton = computed(
  () => detailsItem.value?.type === 'file' && !!detailsItem.value.webUrl,
);

// Only meaningful when showOpenButton is true (guarded by the same file-type check), so this is
// safe to read directly in the template without re-deriving the guard there.
const openButtonLabel = computed(() => {
  const item = detailsItem.value;
  return item && item.type === 'file' ? getOfficeAppLabel(item.name) : '';
});

function handleDownload() {
  const url = itemDetails.value?.downloadUrl;
  // Graph's pre-signed downloadUrl already sets Content-Disposition: attachment server-side, so
  // a plain new-tab navigation is enough — no <a download> anchor trick needed.
  if (url) window.open(url, '_blank');
}

// Graph resolving a URL only means the metadata call finished — the actual image bytes are a
// separate, unfinished fetch at that point. Swapping the icon for an <img> right away lets the
// browser mount it with no known intrinsic size, so it collapses to ~0 height and then snaps to
// full size once decoded. Preloading off-screen first means the icon stays put until the image
// is fully ready, so the swap always lands at its real size — and lets us read its natural
// aspect ratio (see .details-thumbnail-frame below) before it's ever shown.
const loadedThumbnailUrl = ref<string | null>(null);
const loadedThumbnailAspectRatio = ref<number | null>(null);

watch(
  thumbnailUrl,
  (url) => {
    loadedThumbnailUrl.value = null;
    loadedThumbnailAspectRatio.value = null;
    if (!url) return;

    const preload = new Image();
    preload.onload = () => {
      if (thumbnailUrl.value !== url) return;
      loadedThumbnailUrl.value = url;
      loadedThumbnailAspectRatio.value =
        preload.naturalWidth / preload.naturalHeight;
    };
    preload.src = url;
  },
  { immediate: true },
);

// Covers both gaps between "icon" and "thumbnail ready": the Graph metadata call itself, and —
// once that resolves — the image preload above. Only false once the actual pixels are in hand.
const showSpinner = computed(
  () =>
    isThumbnailQueryLoading.value ||
    (!!thumbnailUrl.value && !loadedThumbnailUrl.value),
);

// The frame around the thumbnail is sized in JS, not pure CSS: a div sized only via aspect-ratio
// inside a centered flex parent has no definite dimension to bootstrap from (its only child is a
// 100%-sized <img>, which contributes ~0 to content-based sizing), so it collapses instead of
// computing a fitted size — this bit it did reliably for small source images, causing them to
// render at their natural (tiny) pixel size instead of scaling up. Measuring the mat's actual
// content box and computing the largest rectangle that fits it while preserving the image's real
// aspect ratio sidesteps that CSS auto-sizing gap entirely, for both up- and down-scaling.
const visualRef = ref<HTMLElement | null>(null);
const visualContentSize = ref<{ width: number; height: number } | null>(null);

useResizeObserver(visualRef, ([entry]) => {
  visualContentSize.value = {
    width: entry.contentRect.width,
    height: entry.contentRect.height,
  };
});

const thumbnailFrameSize = computed(() => {
  const container = visualContentSize.value;
  const ratio = loadedThumbnailAspectRatio.value;
  if (!container || !ratio || container.width <= 0 || container.height <= 0)
    return null;

  const containerRatio = container.width / container.height;
  return ratio > containerRatio
    ? { width: container.width, height: container.width / ratio }
    : { width: container.height * ratio, height: container.height };
});
</script>

<template>
  <div v-if="detailsItem" class="details-view">
    <div class="details-header">
      <span class="details-name" :title="detailsItem.name">{{
        detailsItem.name
      }}</span>
      <button
        type="button"
        class="close-button"
        :aria-label="t('fileManager.detailsView.closeDetailsAriaLabel')"
        @click="closeDetails"
      >
        <PhX :size="16" />
      </button>
    </div>
    <button
      ref="visualRef"
      type="button"
      class="details-visual"
      :disabled="!previewClickable"
      @click="handlePreview"
    >
      <Transition name="thumb-fade" mode="out-in">
        <div v-if="showSpinner" key="spinner" class="details-spinner">
          <Spinner />
        </div>
        <div
          v-else-if="loadedThumbnailUrl"
          key="thumb"
          class="details-thumbnail-frame"
          :style="
            thumbnailFrameSize
              ? {
                  width: `${thumbnailFrameSize.width}px`,
                  height: `${thumbnailFrameSize.height}px`,
                }
              : { width: '100%', height: '100%' }
          "
        >
          <img :src="loadedThumbnailUrl" class="details-thumbnail" alt="" />
        </div>
        <div v-else key="icon" class="details-icon">
          <component
            :is="icon?.component"
            :size="128"
            weight="fill"
            :color="icon?.color"
          />
        </div>
      </Transition>
      <div v-if="previewClickable" class="details-preview-badge">
        <PhEye :size="14" weight="bold" />
        <span>{{ t('fileManager.detailsView.viewBadge') }}</span>
      </div>
    </button>
    <div class="details-quick-links">
      <button
        type="button"
        class="details-pill-button details-pill-button--tinted"
        :aria-label="t('fileManager.detailsView.goToFolderAriaLabel', { path: parentPath })"
        :title="parentPath"
        @click="handleGotoFolder"
      >
        <span class="details-pill-icon">
          <PhFolderOpen :size="14" weight="fill" />
        </span>
        <span class="details-pill-label">{{ parentPath }}</span>
      </button>
      <hr
        v-if="showDownloadButton || showOpenButton || showMoreButton"
        class="details-quick-links-divider"
      />
      <div
        v-if="showDownloadButton || showOpenButton || showMoreButton"
        class="details-quick-links-row"
      >
        <button
          v-if="showDownloadButton"
          type="button"
          class="details-pill-button details-pill-button--outline"
          :disabled="isDetailsLoading"
          @click="handleDownload"
        >
          <span class="details-pill-icon">
            <PhDownloadSimple :size="10" weight="bold" />
          </span>
          <span class="details-pill-label">{{ t('fileManager.detailsView.downloadLabel') }}</span>
        </button>
        <button
          v-if="showOpenButton"
          type="button"
          class="details-pill-button details-pill-button--filled"
          @click="emit('open-in-sharepoint', detailsItem)"
        >
          <span class="details-pill-icon">
            <PhArrowSquareOut :size="10" weight="bold" />
          </span>
          <span class="details-pill-label">{{ openButtonLabel }}</span>
        </button>
        <button
          v-if="showMoreButton"
          ref="moreButton"
          type="button"
          class="details-more-button"
          :aria-label="t('fileManager.detailsView.moreOptionsAriaLabel')"
          @click="handleMoreOptions"
        >
          <PhDotsThreeVertical :size="16" weight="bold" />
        </button>
      </div>
    </div>
    <div v-if="itemDetails" class="details-metadata">
      <div class="details-metadata-row">
        <span class="details-metadata-label">{{ t('fileManager.detailsView.sizeLabel') }}</span>
        <span class="details-metadata-value">{{
          formatBytes(itemDetails.size ?? 0)
        }}</span>
      </div>
      <div class="details-metadata-row">
        <span class="details-metadata-label">{{ t('fileManager.detailsView.modifiedLabel') }}</span>
        <span class="details-metadata-value">{{ modifiedDate }}</span>
      </div>
      <div class="details-metadata-row">
        <span class="details-metadata-label">{{ t('fileManager.detailsView.typeLabel') }}</span>
        <span class="details-metadata-value">{{ fileExtension }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.details-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0.5rem 1rem 0.5rem;
  overflow-y: auto;
  /* Query container for the text clamp()s below, so labels scale with this panel's own (definite,
     JS-derived) height rather than the viewport — consistent with .details-visual above, and with
     how a wider/narrower pane already resizes independently via the pool splitter. */
  container-type: size;
  container-name: details-view;
}

.details-header {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding-bottom: 0.5rem;
  background-color: #fff;
}

.details-name {
  min-width: 0;
  padding-top: 0.25rem;
  overflow: hidden;
  font-size: clamp(0.9375rem, 0.85rem + 1.2cqh, 1.1875rem);
  font-weight: 600;
  color: #1f2937;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.close-button {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.375rem;
  color: #6b7280;
  transition: background-color 0.15s ease;
}

.close-button:hover {
  background-color: #f5f5f5;
}

/*
 * The whole mat is now a <button> (not just the thumbnail/icon inside it) so the entire gray area
 * is clickable, not only whichever content happens to be centered in it — gets a11y for free too
 * (focus ring, Enter/Space activation, disabled state, screen-reader semantics). The reset
 * properties below undo default button chrome so it stays visually identical to the div it
 * replaced.
 */
.details-visual {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  /* Percentage of .details-view's own (definite, JS-derived) height rather than a fixed px value,
     so the preview shrinks along with the panel on short screens instead of overflowing it. Clamped
     so it never gets too cramped to be useful, nor balloons past its old fixed size on tall panels. */
  height: clamp(180px, 38%, 320px);
  margin: 0.75rem 0 0.375rem;
  padding: 0.75rem;
  background-color: #f5f5f5;
  border: none;
  border-radius: 1rem;
  font: inherit;
  appearance: none;
  cursor: pointer;
  box-sizing: border-box;
}

.details-visual:disabled {
  cursor: default;
}

/*
 * A dark overlay hinting the whole mat is clickable, covering it regardless of which of the three
 * states (spinner/thumbnail/icon) is currently centered inside — pointer-events: none so it never
 * intercepts the button's own click, and it's excluded entirely for the disabled state (nothing to
 * hover into).
 */
.details-visual::after {
  content: '';
  position: absolute;
  inset: 0;
  background-color: black;
  opacity: 0;
  border-radius: inherit;
  pointer-events: none;
  transition: opacity 0.15s ease;
}

.details-visual:not(:disabled):hover::after {
  opacity: 0.08;
}

/*
 * The gray background is the "mat" a differently-shaped thumbnail sits on. border-radius on an
 * <img> only clips its own layout box, not wherever the letterboxed pixels happen to render
 * inside it — so a landscape image centered in a taller box would show square corners floating in
 * the middle of the mat. This frame's actual width/height come from thumbnailFrameSize (computed
 * in the script from the mat's measured size and the image's real aspect ratio) rather than CSS,
 * so it's sized to exactly match the image's real rectangle — overflow: hidden here then clips
 * precisely at those real edges, and it upscales small source images just as well since sizing is
 * ratio-driven rather than tied to natural pixel dimensions.
 */
.details-spinner,
.details-thumbnail-frame,
.details-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.details-thumbnail-frame {
  border-radius: 0.75rem;
  overflow: hidden;
}

.details-thumbnail {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.details-icon > :deep(svg) {
  padding: 1rem;
  border-radius: 9999px;
  background-color: color-mix(in srgb, var(--primary-color) 8%, transparent);
}

/* Purely informational — sits on top of whichever button state is showing, hinting the visual is
   clickable, so pointer-events: none lets clicks fall through to that button underneath it. */
.details-preview-badge {
  position: absolute;
  right: 1.25rem;
  bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  background-color: rgba(0, 0, 0, 0.6);
  color: var(--white);
  font-size: 0.75rem;
  font-weight: 600;
  pointer-events: none;
}

.details-quick-links {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.details-quick-links-divider {
  width: 100%;
  height: 1px;
  margin: 0rem 0 0.2rem;
  background-color: #e5e7eb;
  border: none;
}

.details-quick-links-row {
  display: flex;
  gap: 0.375rem;
}

.details-quick-links-row .details-pill-button {
  flex: 1;
  min-width: 0;
}

.details-pill-button {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.1875rem 0.5rem 0.1875rem 0.1875rem;
  border: 1px solid var(--primary-color);
  border-radius: 9999px;
  cursor: pointer;
  font: inherit;
  transition: background-color 0.15s ease;
}

.details-pill-button:disabled {
  cursor: default;
  opacity: 0.6;
}

/* Path link — no pill at all, just the icon badge and blue text. */
.details-pill-button--tinted {
  background: none;
  border: none;
  padding: 0;
}

.details-pill-button--tinted:not(:disabled):hover .details-pill-label {
  text-decoration: underline;
}

.details-pill-button--tinted .details-pill-label {
  color: #001c74;
  font-size: clamp(0.75rem, 0.6625rem + 0.6cqh, 0.875rem);
}

.details-pill-button--tinted .details-pill-icon {
  background: none;
  color: var(--primary-color);
}

/* Download — no background tint at all, just the outline. */
.details-pill-button--outline {
  background: none;
}

.details-pill-button--outline:not(:disabled):hover {
  background-color: color-mix(in srgb, var(--primary-color) 8%, transparent);
}

.details-pill-button--outline .details-pill-label {
  color: var(--primary-color);
}

.details-pill-button--outline .details-pill-icon {
  background: none;
  color: var(--primary-color);
}

/* Open in SharePoint — fully filled, so it reads distinctly from Download at a glance. */
.details-pill-button--filled {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.details-pill-button--filled:not(:disabled):hover {
  background-color: color-mix(in srgb, var(--primary-color) 85%, black);
}

.details-pill-button--filled .details-pill-icon {
  background: none;
  color: var(--white);
}

.details-pill-button--filled .details-pill-label {
  color: var(--white);
}

.details-pill-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background-color: var(--primary-color);
  border-radius: 50%;
  color: var(--white);
}

.details-pill-label {
  flex: 1;
  min-width: 0;
  font-size: clamp(0.6875rem, 0.6rem + 0.6cqh, 0.8125rem);
  font-weight: 600;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.thumb-fade-enter-active,
.thumb-fade-leave-active {
  transition: opacity 0.2s ease;
}

.thumb-fade-enter-from,
.thumb-fade-leave-to {
  opacity: 0;
}

.details-metadata {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
}

.details-metadata-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: clamp(0.8125rem, 0.7rem + 1cqh, 0.9375rem);
}

.details-metadata-label {
  color: #6b7280;
  font-weight: 500;
}

.details-metadata-value {
  color: #1f2937;
  font-weight: 600;
  text-align: right;
  word-break: break-word;
}

/* Icon-only, fixed size — deliberately not a .details-pill-button so it doesn't flex-grow
   alongside Download/Open in the row; it's a menu trigger, not another equally-weighted action. */
.details-more-button {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 1.625rem;
  height: 1.625rem;
  padding: 0;
  background: none;
  border: 1px solid #e5e7eb;
  border-radius: 9999px;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.details-more-button:hover {
  background-color: #f5f5f5;
}
</style>
